import * as React from "react";
import { Copy, Pencil } from "lucide-react";

import {
  apiErrorMessage,
  confirmPoll,
  getPoll,
  getPollResults,
  sendPollConfirmation,
  setPollStatus,
  unconfirmPoll,
} from "@/api/client";
import { Poll, PollResults } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { PollConfirmPanel } from "./PollConfirmPanel";
import { PollResultsGrid } from "./PollResultsGrid";

/**
 * One poll after it exists: who answered, what they chose, and the two-step of picking a time and
 * then announcing it.
 *
 * <p>Its own component rather than a tab inside the builder, following {@code BookingPageDetail}:
 * reading results is a different job from editing fields, and it has to stay reachable once the
 * poll is confirmed and no longer editable.</p>
 */
export function PollDetail({
  poll: initial,
  onEdit,
  onChanged,
  onCopyLink,
  onError,
  onNotify,
}: {
  poll: Poll;
  /** Load this poll back into the builder. Absent once it is confirmed. */
  onEdit: (poll: Poll) => void;
  onChanged: (poll: Poll) => void;
  onCopyLink: (path: string) => void;
  onError: (message: string) => void;
  onNotify: (message: string) => void;
}) {
  const [poll, setPoll] = React.useState<Poll>(initial);
  const [results, setResults] = React.useState<PollResults | null>(null);
  const [pendingOptionId, setPendingOptionId] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => setPoll(initial), [initial]);

  const reload = React.useCallback(async () => {
    try {
      const [fresh, res] = await Promise.all([getPoll(poll.id), getPollResults(poll.id)]);
      setPoll(fresh);
      setResults(res);
      onChanged(fresh);
    } catch (e) {
      onError(apiErrorMessage(e, "Couldn't load this poll's results"));
    }
    // onChanged/onError are stable enough in practice; re-running on identity churn would refetch
    // the grid on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll.id]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const run = async (action: () => Promise<Poll>, done: string) => {
    setBusy(true);
    try {
      const next = await action();
      setPoll(next);
      onChanged(next);
      onNotify(done);
      const res = await getPollResults(next.id);
      setResults(res);
    } catch (e) {
      onError(apiErrorMessage(e, "Couldn't do that just now"));
    } finally {
      setBusy(false);
      setPendingOptionId(null);
    }
  };

  const sharePath = `/poll/${poll.slug}`;
  const editable = poll.status !== "CONFIRMED";

  return (
    <div className="space-y-5">
      <div className="comic-shadow space-y-3 rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h2 className="text-lg font-medium text-foreground">{poll.title}</h2>
            <p className="text-sm text-foreground">
              {poll.options.length} times · {poll.duration_minutes} minutes ·{" "}
              {poll.respondent_count} {poll.respondent_count === 1 ? "response" : "responses"}
              {poll.respond_by && ` · answers close ${poll.respond_by}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => onCopyLink(sharePath)}>
              <Copy className="mr-1.5 h-4 w-4" /> Copy link
            </Button>
            {editable && (
              <Button variant="outline" onClick={() => onEdit(poll)}>
                <Pencil className="mr-1.5 h-4 w-4" /> Edit
              </Button>
            )}
            {editable && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  run(
                    () => setPollStatus(poll.id, poll.status === "OPEN" ? "CLOSED" : "OPEN"),
                    poll.status === "OPEN" ? "Voting closed" : "Voting reopened",
                  )
                }
                className={cn(
                  "rounded-full border border-foreground px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted",
                )}
              >
                {poll.status === "OPEN" ? "Close voting" : "Reopen voting"}
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-foreground">
          <span className="font-medium">{window.location.origin}</span>
          {sharePath}
        </p>
      </div>

      {results && (
        <>
          <PollConfirmPanel
            poll={poll}
            results={results}
            pendingOptionId={pendingOptionId}
            onCancelChoice={() => setPendingOptionId(null)}
            onConfirm={(optionId) => run(() => confirmPoll(poll.id, optionId), "Time confirmed")}
            onUnconfirm={() => run(() => unconfirmPoll(poll.id), "Confirmation withdrawn")}
            onSend={() => run(() => sendPollConfirmation(poll.id), "Confirmation sent")}
            busy={busy}
          />

          <div className="comic-shadow rounded-xl border border-border bg-card p-5">
            <PollResultsGrid
              results={results}
              confirmedOptionId={poll.confirmed_option_id}
              // Once a time is confirmed the grid stops offering choices and becomes the record of
              // how the decision was made. Withdrawing brings the choosers back.
              onChoose={
                poll.status === "CONFIRMED" ? undefined : (id) => setPendingOptionId(id)
              }
            />
          </div>
        </>
      )}
    </div>
  );
}
